
//////////////////////////////////////////////////////////////////////////////////////////
// Code Imports
//////////////////////////////////////////////////////////////////////////////////////////

import Ractive from "ractive";
import _ from "lodash";
import $ from "jquery";

import APICaller from "utils/api-caller";
import Formatter from "utils/formatter";
import CSVReport from "utils/csv-builder";

import GenericItemSelector from "ui/generic-item-selector";
import DateRangePicker from "ui/date-range-picker";

import ResourceActivityReport from "report/resource-activity-report";
import ProjectActivityReport from "report/project-activity-report";
import EstimatesVsActualsReport from "report/estimates-vs-actual-report";

import page from "router/page";

require('selectize/selectize');

//////////////////////////////////////////////////////////////////////////////////////////
// Declarations and Constants
//////////////////////////////////////////////////////////////////////////////////////////

const API_URL             = window.API_URL;

//////////////////////////////////////////////////////////////////////////////////////////

const apiCaller           = new APICaller(API_URL);
const formatter           = new Formatter();

//////////////////////////////////////////////////////////////////////////////////////////
// Utilities
//////////////////////////////////////////////////////////////////////////////////////////

let reporters = {};

//////////////////////////////////////////////////////////////////////////////////////////
// Components
//////////////////////////////////////////////////////////////////////////////////////////

let ReportSubjectCard = Ractive.extend({
    template: '#report-subject-card'
})

let ResourceEffortDashboard = Ractive.extend({
    template: '#resource-effort-dashboard'
});

let ProjectEffortDashboard = Ractive.extend({
    template: '#project-effort-dashboard'
});

let ProjectActivityBreakdown = Ractive.extend({
    template: '#project-activity-breakdown'
});

let ResourceActivityBreakdown = Ractive.extend({
    template: '#resource-activity-breakdown'
});

let TimelineBreakdown = Ractive.extend({
    template: '#timeline-breakdown',
    refit: (dates) => {
        // ---------------------------------------------------------------------
        // Width of the timeblock, and row offset
        const TIME_BLOCK_WIDTH    = 54;
        const TIMELINE_ROW_OFFSET = 16;

        $('.timeline-row').css('width', dates.length * TIME_BLOCK_WIDTH + TIMELINE_ROW_OFFSET);        
    }
});

let ProjectBreakdown = Ractive.extend({
    template: '#project-breakdown'
});

let ResourceBreakdown = Ractive.extend({
    template: '#resource-breakdown'
});

let ProjectSelector = GenericItemSelector(
    'project',
    (projectId, app) => {
        let projects = app.get('projects.lookup');
        let project  = projects[projectId];
        
        app.set('active.project', project);
    }
);

let ResourceSelector = GenericItemSelector(
    'resource', 
    (bindname, app) => {
        let users    = app.get('resources.lookup');
        let user     = users[bindname];

        app.set('active.resource', user);
    }
);

let app = Ractive({
    target: '#app',
    template: '#main-container',
    formatter: formatter,
    components: {
        ReportSubjectCard         : ReportSubjectCard,
        ResourceEffortDashboard   : ResourceEffortDashboard,
        ProjectEffortDashboard    : ProjectEffortDashboard,
        ProjectActivityBreakdown  : ProjectActivityBreakdown,
        ResourceActivityBreakdown : ResourceActivityBreakdown,
        TimelineBreakdown         : TimelineBreakdown,
        ProjectBreakdown          : ProjectBreakdown,
        ResourceBreakdown         : ResourceBreakdown,
        ProjectSelector           : ProjectSelector,
        ResourceSelector          : ResourceSelector,
        DateRangePicker           : DateRangePicker
    },
    oninit: app__onInit
});

//////////////////////////////////////////////////////////////////////////////////////////
// Application lifecycle
//////////////////////////////////////////////////////////////////////////////////////////

function app__onInit() {

    console.debug('@onInit – Initializing application');

    let app = this.root;

    //----------------------------------------------------------------
    // Define all system level routines here
    
    app.initializeReport = (name, title) => {
        app.set('REPORT', {
            NAME   : name,
            TITLE  : title,
            LOADED : false
        });
    };
    
    app.reportIsLoaded = () => {
        app.set('REPORT.LOADED', true);
    };

    app.hasRecords = (response) => {
        if (response) {
            app.set('REPORT.DATASET', response);
        }
        
        return (response) ?
            app.set('REPORT.HAS_RECORDS', (response.length > 0)) :
            app.get('REPORT.HAS_RECORDS');

    };

    app.getReportName = () => {
        return app.get('REPORT.NAME');
    };

    //----------------------------------------------------------------
    app.initializeReport('', '');
}

(async () => {
    await loadData();

    reporters = [
        new ResourceActivityReport(app, apiCaller),
        new ProjectActivityReport(app, apiCaller),
        new EstimatesVsActualsReport(app, apiCaller)
    ];

    bindEvents();
    defineRoutes();
})();

async function loadData() {

    console.debug('#loadData - Loading Data');

    let users             = await apiCaller.user.getAllUsers();
    let activeUsers       = await apiCaller.user.getActiveUsers();
    let activeProjects    = await apiCaller.project.getActiveProjects();
    
    //----------------------------------------------------------------------

    app.set('resources.list' , activeUsers.users);
    app.set('resources.lookup', _.keyBy(users.users, 'bindname'));

    //----------------------------------------------------------------------

    let projectGroups     = _.partition(activeProjects.projects, (project) => {
        return project.name.match(/internal/ig);
    });

    let groupedProjectByType = {
        'Billable Projects': projectGroups[1],
        'Internal Projects': projectGroups[0]
    };

    app.set('projects.list', activeProjects.projects);
    app.set('projects.lookup', _.keyBy(activeProjects.projects, 'ProjectID'));

    app.set('projects.group', groupedProjectByType);
    app.set('projects.group.keys', _.keys(groupedProjectByType));

}

//////////////////////////////////////////////////////////////////////////////////////////

/**
 * Define all application URL endpoints
 */
function defineRoutes() {
    
    console.debug('#defineRoutes – Defining Routes');

    _.each(reporters, (reporter) => {
        reporter.registerRoutes();
    })

    page({hashbang:true});
}

//////////////////////////////////////////////////////////////////////////////////////////
// Event Handlers
//////////////////////////////////////////////////////////////////////////////////////////

function onClick__generateReport(context) {

    let app  = context.ractive,
        name = app.get('REPORT.NAME');
    
    if (name === 'resource') {
        if (!app.get('active.resource')) return;

        let resource  = app.get('active.resource').bindname,
            dateRange = app.get('dateRange');

        page(`/activity/resource/${resource}/${dateRange.from}/${dateRange.to}`);
    }

    if (name === 'project') {
        if (!app.get('active.project')) return;

        let projectId  = app.get('active.project').ProjectID,
            dateRange = app.get('dateRange');

        page(`/activity/project/${projectId}/${dateRange.from}/${dateRange.to}`);
    }
}

function onClick_downloadReport(context) {
    
    let app        = context.ractive,
        reportType = app.get('REPORT.NAME'),
        columns    = [
            "Date", 
            "Project ID", 
            "Project Name", 
            "Task ID", 
            "Task Name", 
            "Resource",
            "Time Spent"
        ];

    let report   = new CSVReport(reportType, app);
    let filename = report.createFilename();
    let csvData  = report.assembleData(columns);

    if (!filename && !csvData) return;

    report.downloadFile(filename, csvData);
}

function bindEvents() {
    app.on({
        'generate-report': onClick__generateReport,
        'download-report': onClick_downloadReport
    });
}
